import React, { useEffect, useState } from 'react';
import { DownloadSimple, X, Share, House } from '@phosphor-icons/react';

const DISMISSED_KEY = 'defectr:install-hint:v1:dismissed';

/**
 * "Install DefectR" hint card — appears once on the home page for users
 * who haven't installed the PWA. Three states:
 *  - Hidden: user is already running standalone (installed) OR has dismissed
 *  - Android / Chrome: tappable button that fires the native installPrompt
 *  - iOS Safari: instructional card showing the manual share-sheet path
 *    (since iOS doesn't expose programmatic prompt yet)
 */
export default function InstallHintCard() {
    const [show, setShow] = useState(false);
    const [installPrompt, setInstallPrompt] = useState(null);
    const [platform, setPlatform] = useState('other');

    useEffect(() => {
        // Already installed → never show
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
            || window.navigator.standalone === true;
        if (isStandalone) return;

        // User dismissed previously → never show again
        try {
            if (localStorage.getItem(DISMISSED_KEY)) return;
        } catch { /* private mode — show anyway */ }

        const ua = (navigator.userAgent || '').toLowerCase();
        const isIOS = /iphone|ipad|ipod/.test(ua)
            || (ua.includes('mac') && 'ontouchend' in document);
        const isAndroid = /android/.test(ua);

        if (isIOS) {
            setPlatform('ios');
            setShow(true);
        } else if (isAndroid) {
            setPlatform('android');
            // Wait for the native beforeinstallprompt event
            const handler = (e) => {
                e.preventDefault();
                setInstallPrompt(e);
                setShow(true);
            };
            window.addEventListener('beforeinstallprompt', handler);
            return () => window.removeEventListener('beforeinstallprompt', handler);
        }
        // Desktop or unknown — don't show
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(DISMISSED_KEY, '1'); } catch { /* noop */ }
        setShow(false);
    };

    const triggerInstall = async () => {
        if (!installPrompt) return;
        installPrompt.prompt();
        try {
            const choice = await installPrompt.userChoice;
            if (choice.outcome === 'accepted') dismiss();
        } catch { /* noop */ }
    };

    if (!show) return null;

    return (
        <div
            className="border border-[#1FA5CF] bg-gradient-to-br from-[#E8F5FB] to-white p-4 sm:p-5 mb-6 relative"
            data-testid="install-hint"
        >
            <button
                type="button"
                onClick={dismiss}
                aria-label="Dismiss install hint"
                data-testid="dismiss-install-hint"
                className="absolute top-2 right-2 p-1.5 hover:bg-white/60 active:scale-95 transition-all duration-150"
            >
                <X size={14} weight="bold" />
            </button>

            <div className="flex items-start gap-3">
                <div className="flex-none w-10 h-10 bg-[#1FA5CF] flex items-center justify-center text-white">
                    <DownloadSimple size={20} weight="bold" />
                </div>
                <div className="flex-1 min-w-0 pr-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1787AB] font-bold">
                        One-tap access
                    </div>
                    <h3 className="font-display font-bold text-base mt-0.5">
                        Install DefectR on your home screen
                    </h3>
                    <p className="text-sm text-[#52525B] mt-1">
                        {platform === 'ios'
                            ? 'Faster than a bookmark. Snag from your home screen on viewings — no browser address bar.'
                            : 'Faster than a bookmark. Snag from your home screen on viewings — full-screen, offline-capable.'}
                    </p>

                    {platform === 'android' && installPrompt && (
                        <button
                            type="button"
                            onClick={triggerInstall}
                            data-testid="install-prompt-btn"
                            className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#1FA5CF] text-white font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150"
                        >
                            <DownloadSimple size={14} weight="bold" />
                            Install
                        </button>
                    )}

                    {platform === 'ios' && (
                        <div
                            className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-[#09090B]"
                            data-testid="ios-install-steps"
                        >
                            <Step n="1" icon={Share}>Tap the <strong>Share</strong> icon in Safari</Step>
                            <Step n="2" icon={House}>Choose <strong>Add to Home Screen</strong></Step>
                            <Step n="3" icon={DownloadSimple}>Tap <strong>Add</strong> — done</Step>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Step({ n, icon: Icon, children }) {
    return (
        <div className="flex items-start gap-2 p-2 bg-white border border-[#E4E4E7]">
            <span className="flex-none w-5 h-5 flex items-center justify-center bg-[#1FA5CF] text-white font-mono text-[10px] font-bold">
                {n}
            </span>
            <span className="flex-1 leading-tight">
                <Icon size={12} weight="bold" className="inline mr-1 mb-0.5" />
                {children}
            </span>
        </div>
    );
}
